import { Skeleton } from '@cibli/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { useSearchParams } from 'react-router';
import remarkGfm from 'remark-gfm';

import type { CGU } from '@/services/cgu';

import MainLayout from '@/features/common/components/MainLayout';
import { useI18nDirection } from '@/hooks/useI18nDirection';
import { markdownComponents } from '@/lib/markdown';
import { setApiLanguage } from '@/services/api';
import { getLatestCGU } from '@/services/cgu';

export default function LegalsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [cgu, setCgu] = useState<CGU | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const directionDivRef = useI18nDirection<HTMLDivElement>();

  useEffect(() => {
    const fetchCGU = async () => {
      const language = searchParams.get('language') || 'fr-FR';
      try {
        setLoading(true);
        setError(null);
        setApiLanguage(language);
        const latestCGU = await getLatestCGU();
        setCgu(latestCGU);
      } catch (err) {
        console.error('Failed to fetch CGU:', err);
        setError(
          t('legals.error', {
            language: language,
            defaultValue: `Unable to load CGU for language: ${language}`,
          }),
        );
      } finally {
        setLoading(false);
      }
    };

    void fetchCGU();
  }, [searchParams, t]);

  return (
    <MainLayout
      padding="p-4 sm:p-8 gap-8"
      className="h-[calc(100dvh_-_72px)] sm:h-[calc(100dvh_-_96px)]"
    >
      <div className="bg-white rounded-lg flex-1 w-full overflow-y-auto">
        <div className="p-6 text-left" ref={directionDivRef}>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center">
              <p className="mb-4">{error}</p>
            </div>
          ) : cgu ? (
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {cgu.content}
            </Markdown>
          ) : (
            <div className="text-center text-gray-500">
              {t('legals.notFound', {
                defaultValue: 'CGU not found',
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
